/**
 * Fleet overview.
 *
 * The headline is not the stat row — it is `missing_doctor_rows`. A center
 * whose subdomain has no matching `doctor` row in the shared Postgres probes
 * perfectly healthy: Orthanc is up, the container is running, DNS resolves. It
 * fails the instant a real user opens it — and if no doctor holds that id yet,
 * the doctor who is one day registered with it inherits the center's images.
 * Creating a center now finds or registers its doctor, so what remains is
 * legacy centers and doctors removed since; there is no form that fixes it,
 * only a center to review or remove. So it is called out above everything
 * else, in words, before any number.
 *
 * The redesign turns that priority into the page's structure. Everything that
 * needs a human is gathered into one panel at the top, ranked; everything that
 * is fine is rendered quietly below it. When nothing is wrong the attention
 * panel collapses to a single line and the page has no colour in it at all —
 * which is the point. An operations console that always looks alarming teaches
 * people to stop reading it.
 *
 * ── Compaction pass ────────────────────────────────────────────────────────
 * The structure above is unchanged; the prose around it is gone. What changed:
 *
 *   The page-header subtitle (loopback-vs-Caddy explainer) and both card
 *   subtitles are cut. They taught the model once and then cost vertical space
 *   on every visit. The "doctor-row check did not run" callout stays — that is
 *   honesty about an unrun check, not a tutorial.
 *
 *   Attention items are one line each — mark, title, the *names* involved,
 *   action — and the explanatory paragraph under each is gone. What survives in
 *   the detail is the part a stat tile cannot carry: *which* centers, *which*
 *   nodes. Above three items the tail folds into a `<details>`; nothing is
 *   hidden from the ranking, only from the resting height. There is no age
 *   column: these items are derived from the current summary, not from events,
 *   so no per-item timestamp exists and inventing one would be worse than
 *   omitting it. The fleet-wide "checked N ago" lives in the header.
 *
 *   The `warnings` attention item is dropped outright. Warnings are non-blocking
 *   by definition and the KPI strip now carries a clickable Warnings tile
 *   pointing at the same `/issues` page — two rows with the same destination and
 *   the same verb is the "identical framing" the strip is supposed to replace.
 *   It stays dropped even when blocking > 0: the blocking item already sends the
 *   operator to `/issues`, where the warnings are sitting next to the blockers.
 *   The consequence is that the header badge now counts only things that need a
 *   human, which is what the badge claims to count.
 *
 *   The KPI strip gained average latency (mean of `latency_ms` over the centers
 *   that actually answered — a failed probe has no latency and is excluded, and
 *   the hint names the sample size so the mean cannot be read as fleet-wide),
 *   total studies, and warnings; tiles with a destination are now links.
 *   Nothing tints latency: the API publishes no threshold, so a red number would
 *   be this page's opinion dressed as telemetry.
 *
 *   The missing-doctor-rows tile reads UNKNOWN, not 0, when the check did not
 *   run. An unconfigured check rendering as a clean zero is precisely the trap
 *   `doctor_check_configured` exists to close.
 */

import { useMemo } from 'react';
import { Link } from 'react-router-dom';

import { Badge, StatusBadge } from '../components/Badge';
import { Callout } from '../components/Callout';
import { Freshness } from '../components/Freshness';
import { PageHeader } from '../components/PageHeader';
import { StatGrid, StatTile, type MetricTone } from '../components/StatTile';
import {
  EmptyBlock,
  ErrorBlock,
  SkeletonRows,
  StaleBanner,
} from '../components/States';
import { Card, CardBody, CardHeader } from '../components/ui/Card';
import { ChevronRightIcon } from '../components/ui/Icon';
import { formatLatency, formatNumber, pluralise, UNKNOWN } from '../lib/format';
import { useResource } from '../lib/useResource';
import type {
  EdgeNodeListResponse,
  FleetHealthResponse,
  FleetSummaryResponse,
  OrthancHealth,
} from '../types/api';
import '../styles/page-overview.css';

const REFRESH_MS = 30_000;

/** How many attention items stand open before the tail folds away. */
const ATTENTION_OPEN = 3;

/** How many names an attention line lists before it says "+N more". */
const NAME_LIMIT = 4;

/** Problems first: the point of this page is triage, not an alphabet. */
const STATE_RANK: Record<string, number> = {
  down: 0,
  degraded: 1,
  unknown: 2,
  up: 3,
};

function triageOrder(a: OrthancHealth, b: OrthancHealth): number {
  const aDoctorMissing = a.doctor_row_present === false ? 0 : 1;
  const bDoctorMissing = b.doctor_row_present === false ? 0 : 1;
  const aRank = Math.min(STATE_RANK[a.state] ?? 2, aDoctorMissing === 0 ? 0 : 9);
  const bRank = Math.min(STATE_RANK[b.state] ?? 2, bDoctorMissing === 0 ? 0 : 9);
  if (aRank !== bRank) return aRank - bRank;
  if (aDoctorMissing !== bDoctorMissing) return aDoctorMissing - bDoctorMissing;
  return a.slug.localeCompare(b.slug);
}

/** "alpha, beta, gamma, delta, +3 more" — the detail half of one line. */
function nameList(names: readonly string[]): string {
  const head = names.slice(0, NAME_LIMIT).join(', ');
  const rest = names.length - NAME_LIMIT;
  return rest > 0 ? `${head}, +${rest} more` : head;
}

/** One line in the attention panel. Nothing here is decorative. */
interface Attention {
  id: string;
  tone: 'critical' | 'warning';
  title: string;
  detail: string;
  to: string;
  action: string;
}

export function FleetOverviewPage() {
  const summary = useResource<FleetSummaryResponse>('/api/fleet/summary', {
    refreshMs: REFRESH_MS,
  });
  const health = useResource<FleetHealthResponse>('/api/fleet/centers', {
    refreshMs: REFRESH_MS,
  });
  // Already served, previously unused here: the edge fleet is half the estate
  // and its absence from the overview meant an offline hospital node showed up
  // nowhere until someone opened its page.
  const edge = useResource<EdgeNodeListResponse>('/api/edge-nodes', {
    refreshMs: REFRESH_MS,
  });

  const reloadAll = () => {
    summary.reload();
    health.reload();
    edge.reload();
  };

  const data = summary.data;
  const centers = useMemo(
    () => (health.data ? [...health.data.centers].sort(triageOrder) : []),
    [health.data],
  );

  // The server states outright whether the doctor-row check ran. Inferring it
  // from "every center reports null" also worked, but only once the health
  // request had landed and never for an empty fleet — and getting it wrong
  // means an unconfigured check renders as a passing one.
  const doctorCheckUnavailable = data !== null && !data.doctor_check_configured;
  const missing = data?.missing_doctor_rows ?? [];

  const nodes = edge.data?.nodes ?? [];
  const nodesOffline = nodes.filter(
    (n) => n.liveness === 'offline' || n.liveness === 'never',
  );
  const nodesOnline = nodes.filter((n) => n.liveness === 'online');
  // An unreadable registry (200 with `error` set) or a failed request is not
  // a known zero: the KPI says UNKNOWN, as CentersPage does.
  const edgeUnknown = !edge.data || Boolean(edge.data.error);

  // Mean round-trip over the centers that answered. Nulls are dropped rather
  // than counted as zero: a probe that failed did not come back instantly, it
  // did not come back. `sampled` is shown beside the number so a mean over two
  // of nine centers cannot be mistaken for the fleet's.
  const latency = useMemo(() => {
    const samples = centers
      .map((c) => c.latency_ms)
      .filter((v): v is number => v !== null && Number.isFinite(v));
    if (samples.length === 0) return { mean: null as number | null, sampled: 0 };
    const total = samples.reduce((sum, v) => sum + v, 0);
    return { mean: total / samples.length, sampled: samples.length };
  }, [centers]);

  const attention = useMemo<Attention[]>(() => {
    const items: Attention[] = [];

    if (missing.length > 0) {
      items.push({
        id: 'doctor-rows',
        tone: 'critical',
        title: `${missing.length} ${pluralise(missing.length, 'center')} ${
          missing.length === 1 ? 'has' : 'have'
        } no doctor row`,
        detail: nameList(missing),
        to: '/centers',
        action: 'Review',
      });
    }

    // A check that did not run is not a check that passed: without this item
    // the page would show "All clear" beside the callout saying it is unknown.
    if (doctorCheckUnavailable) {
      items.push({
        id: 'doctor-check',
        tone: 'warning',
        title: 'The doctor-row check did not run',
        detail: 'The shared Clinic360 database could not be queried; treat doctor rows as unknown',
        to: '/centers',
        action: 'Review',
      });
    }

    const broken = centers.filter((c) => c.state === 'down' || c.state === 'degraded');
    if (broken.length > 0) {
      items.push({
        id: 'centers-down',
        tone: 'critical',
        title: `${broken.length} ${pluralise(broken.length, 'center')} not serving`,
        detail: nameList(broken.map((c) => `${c.slug} (${c.state})`)),
        to: '/centers',
        action: 'Inspect',
      });
    } else if (!health.data && (data?.down ?? 0) > 0) {
      // Per-center health did not load, but the summary still counts centers
      // down: say so rather than "Every center is serving".
      const down = data?.down ?? 0;
      items.push({
        id: 'centers-down',
        tone: 'critical',
        title: `${down} ${pluralise(down, 'center')} not serving`,
        detail: 'per-center health did not load',
        to: '/centers',
        action: 'Inspect',
      });
    }

    if ((data?.blocking_findings ?? 0) > 0) {
      items.push({
        id: 'blocking',
        tone: 'critical',
        title: `${formatNumber(data?.blocking_findings)} blocking ${pluralise(
          data?.blocking_findings ?? 0,
          'finding',
        )}`,
        detail: 'No apply can run while one stands.',
        to: '/issues',
        action: 'Review',
      });
    }

    if (nodesOffline.length > 0) {
      items.push({
        id: 'nodes-offline',
        tone: 'warning',
        title: `${nodesOffline.length} edge ${pluralise(
          nodesOffline.length,
          'node',
        )} not reporting`,
        detail: nameList(nodesOffline.map((n) => n.label ?? n.node_uuid)),
        to: '/edge-nodes',
        action: 'Inspect',
      });
    }

    // No `warnings` item: the KPI strip carries a clickable Warnings tile to the
    // same page, and a warning is by definition not something that must be
    // handled now. See the header comment for why that holds when blocking > 0.

    return items;
  }, [missing, centers, data, nodesOffline, doctorCheckUnavailable, health.data]);

  const worst = attention.find((a) => a.tone === 'critical')
    ? 'critical'
    : attention.length > 0
      ? 'warning'
      : 'good';

  const open = attention.slice(0, ATTENTION_OPEN);
  const folded = attention.slice(ATTENTION_OPEN);

  return (
    <>
      <PageHeader
        title="Fleet overview"
        status={
          data ? (
            <Badge tone={worst === 'good' ? 'good' : worst}>
              {worst === 'good'
                ? 'All clear'
                : `${attention.length} ${pluralise(attention.length, 'item')} ${
                    attention.length === 1 ? 'needs' : 'need'
                  } attention`}
            </Badge>
          ) : null
        }
        actions={
          <Freshness
            checkedAt={data?.checked_at ?? health.data?.checked_at ?? null}
            refreshing={summary.refreshing || health.refreshing}
            onRefresh={reloadAll}
            staleAfterMs={REFRESH_MS * 3}
          />
        }
      />

      {/* ── what needs a human ────────────────────────────────────────────
          Ranked, and rendered before any number. When it is empty it becomes
          a single quiet line rather than a green banner — a console that
          celebrates normality is a console people stop reading. The card has
          no header: the page-header badge already names it and counts it, and
          repeating that costs a row of pixels on every load. */}
      {data ? (
        attention.length > 0 ? (
          <Card tone={worst === 'critical' ? 'critical' : 'warning'} aria-label="Needs attention">
            <CardBody flush>
              <ul className="ov-att">
                {open.map((item) => (
                  <AttentionLine key={item.id} item={item} />
                ))}
              </ul>
              {folded.length > 0 ? (
                <details className="ov-more">
                  <summary className="ov-more__summary">
                    {folded.length} more {pluralise(folded.length, 'item')}
                  </summary>
                  <ul className="ov-att">
                    {folded.map((item) => (
                      <AttentionLine key={item.id} item={item} />
                    ))}
                  </ul>
                </details>
              ) : null}
            </CardBody>
          </Card>
        ) : (
          <p className="all-clear">
            <Badge tone="good">All clear</Badge>
            <span className="muted">
              Every center is serving, no blocking findings, every subdomain has a clinic.
            </span>
          </p>
        )
      ) : null}

      {doctorCheckUnavailable ? (
        <Callout tone="neutral" title="The doctor-row check did not run">
          <p>
            The controller has no connection to the shared Clinic360 database
            (<code>FLEET_CLINIC_DSN</code>), so no center could be checked for a
            matching <code>doctor</code> row. Treat this as unknown rather than clear.
          </p>
        </Callout>
      ) : null}

      {/* ── headline numbers ─────────────────────────────────────────────
          Every tile with somewhere to go is a link; the rest are plain. */}
      <section aria-labelledby="fleet-summary-heading" className="ov-kpis">
        <h2 className="visually-hidden" id="fleet-summary-heading">
          Fleet summary
        </h2>

        {summary.loading ? (
          <SkeletonRows rows={2} />
        ) : summary.error && !data ? (
          <ErrorBlock what="the fleet summary" error={summary.error} onRetry={summary.reload} />
        ) : data ? (
          <div className="stack">
            {summary.error ? (
              <StaleBanner error={summary.error} onRetry={summary.reload} />
            ) : null}
            <StatGrid>
              <KpiLink
                to="/centers"
                label="Centers"
                value={formatNumber(data.total_centers)}
                hint={
                  data.unknown > 0
                    ? `${formatNumber(data.unknown)} never probed`
                    : 'all probed'
                }
              />
              <KpiLink
                to="/centers"
                label="Serving"
                value={formatNumber(data.up)}
                tone={data.up === data.total_centers ? 'good' : 'neutral'}
                hint={`of ${formatNumber(data.total_centers)}`}
              />
              {/* `down` already includes `degraded`, so the two are never
                  added — the split goes in the hint instead. */}
              <KpiLink
                to="/centers"
                label="Not serving"
                value={formatNumber(data.down)}
                tone={data.down > 0 ? 'critical' : 'neutral'}
                hint={
                  data.degraded > 0
                    ? `${formatNumber(data.degraded)} degraded`
                    : data.down > 0
                      ? 'none degraded'
                      : 'all answered'
                }
              />
              {/* No tone: the contract publishes no latency threshold, so any
                  colour here would be this page's opinion, not the fleet's. */}
              <StatTile
                label="Avg latency"
                value={formatLatency(latency.mean)}
                hint={
                  latency.sampled > 0
                    ? `over ${formatNumber(latency.sampled)} probed`
                    : 'no probe answered'
                }
              />
              <StatTile
                label="Studies"
                value={formatNumber(data.total_studies)}
                hint={data.total_studies !== null ? 'fleet total' : 'unavailable'}
              />
              <KpiLink
                to="/edge-nodes"
                label="Edge nodes"
                value={
                  edgeUnknown
                    ? UNKNOWN
                    : `${formatNumber(nodesOnline.length)}/${formatNumber(nodes.length)}`
                }
                tone={
                  edgeUnknown || nodes.length === 0
                    ? 'neutral'
                    : nodesOffline.length > 0
                      ? 'warning'
                      : 'good'
                }
                hint={
                  edge.data?.error
                    ? 'registry unreadable'
                    : edge.error
                      ? 'could not load'
                      : !edge.data
                        ? 'loading'
                        : nodes.length === 0
                          ? 'none enrolled'
                          : 'online now'
                }
              />
              <KpiLink
                to="/issues"
                label="Blocking"
                value={formatNumber(data.blocking_findings)}
                tone={data.blocking_findings > 0 ? 'critical' : 'neutral'}
                hint={data.blocking_findings > 0 ? 'apply is blocked' : 'apply is clear'}
              />
              <KpiLink
                to="/issues"
                label="Warnings"
                value={formatNumber(data.warning_findings)}
                tone={data.warning_findings > 0 ? 'warning' : 'neutral'}
                hint="not blocking"
              />
              {/* UNKNOWN, never 0, when the check did not run — an unconfigured
                  check that renders as a clean zero is the exact failure this
                  page exists to prevent. */}
              <KpiLink
                to="/centers"
                label="No doctor row"
                value={doctorCheckUnavailable ? UNKNOWN : formatNumber(missing.length)}
                tone={
                  doctorCheckUnavailable
                    ? 'neutral'
                    : missing.length > 0
                      ? 'critical'
                      : 'neutral'
                }
                hint={
                  doctorCheckUnavailable
                    ? 'check did not run'
                    : missing.length > 0
                      ? 'healthy, unusable'
                      : 'every subdomain ok'
                }
              />
            </StatGrid>
          </div>
        ) : null}
      </section>

      {/* ── per-center health ───────────────────────────────────────────── */}
      <Card flush>
        <CardHeader
          title="Centers"
          count={health.data ? formatNumber(health.data.centers.length) : undefined}
          actions={
            <>
              {/* The sort order is not obvious from the rows, so it is stated —
                  on the header's own line, where it costs no height. */}
              <span className="ov-note">Most urgent first</span>
              <Link to="/centers" className="card__link">
                All centers <ChevronRightIcon size={14} />
              </Link>
            </>
          }
        />
        {health.loading ? (
          <CardBody>
            <SkeletonRows rows={4} />
          </CardBody>
        ) : health.error && !health.data ? (
          <CardBody>
            <ErrorBlock what="live center health" error={health.error} onRetry={health.reload} />
          </CardBody>
        ) : health.data ? (
          <>
            {health.error ? (
              <CardBody>
                <StaleBanner error={health.error} onRetry={health.reload} />
              </CardBody>
            ) : null}
            {centers.length === 0 ? (
              <CardBody>
                <EmptyBlock title="No centers are defined">
                  The controller read its <code>centers.yml</code> but found no centers in
                  it. This is the fleet&rsquo;s own configuration, not a permissions problem.
                </EmptyBlock>
              </CardBody>
            ) : (
              <div className="table-scroll" tabIndex={0} role="region" aria-label="Center health">
                <table className="data-table">
                  <thead>
                    <tr>
                      <th scope="col">Center</th>
                      <th scope="col">State</th>
                      <th scope="col" className="numeric">Studies</th>
                      <th scope="col" className="numeric">Latency</th>
                      <th scope="col">Orthanc</th>
                    </tr>
                  </thead>
                  <tbody>
                    {centers.map((center) => (
                      <CenterRow key={center.slug} center={center} />
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </>
        ) : null}
      </Card>
    </>
  );
}

/**
 * One attention item on one line: mark, what, who, where to go.
 *
 * The detail truncates rather than wrapping — the full string is on the element
 * as a title so nothing is lost, and a line that cannot grow is what keeps the
 * panel a fixed, predictable height however bad the day is.
 */
function AttentionLine({ item }: { item: Attention }) {
  return (
    <li className={`ov-att__item ov-att__item--${item.tone}`}>
      <span className="ov-att__mark" aria-hidden="true" />
      <span className="ov-att__title">{item.title}</span>
      <span className="ov-att__detail" title={item.detail}>
        {item.detail}
      </span>
      <Link to={item.to} className="ov-att__action">
        {item.action}
        <ChevronRightIcon size={13} />
      </Link>
    </li>
  );
}

/**
 * A KPI tile that goes somewhere. Same markup as `StatTile`, wearing an anchor:
 * a filter or a destination behind a number should be reachable by keyboard,
 * middle-click and the browser's own history, which a click handler is not.
 */
function KpiLink({
  to,
  label,
  value,
  hint,
  tone,
}: {
  to: string;
  label: string;
  value: string;
  hint?: string;
  tone?: MetricTone;
}) {
  return (
    <Link to={to} className={`metric${tone ? ` metric--${tone}` : ''}`}>
      <div className="metric__head">
        <span className="metric__label">{label}</span>
      </div>
      <span className="metric__value">{value}</span>
      {hint ? <span className="metric__hint">{hint}</span> : null}
    </Link>
  );
}

function CenterRow({ center }: { center: OrthancHealth }) {
  const doctorMissing = center.doctor_row_present === false;
  const bad = center.state === 'down' || center.state === 'degraded';

  return (
    <tr className={bad || doctorMissing ? 'center-tr--flagged' : undefined}>
      <th scope="row" className="center-tr__name">
        <Link to={`/centers/${center.slug}`}>{center.slug}</Link>
      </th>
      <td>
        <span className="row">
          <StatusBadge state={center.state} />
          {doctorMissing ? <Badge tone="warning" small>No doctor row</Badge> : null}
        </span>
      </td>
      {/* A probe error replaces the numbers rather than sitting beside them:
          there are no numbers when the probe failed, and rendering "0 studies"
          next to an error is exactly the lie format.ts exists to prevent. */}
      {center.error ? (
        <td colSpan={3} className="center-tr__error" title={center.error}>
          {center.error}
        </td>
      ) : (
        <>
          <td className="numeric">{formatNumber(center.studies)}</td>
          <td className="numeric">{formatLatency(center.latency_ms)}</td>
          <td className="mono">{center.orthanc_version ?? UNKNOWN}</td>
        </>
      )}
    </tr>
  );
}
